import { theme } from '@Guidelines/Theme/theme';
import { css, type SerializedStyles } from '@emotion/react';

/**
 * The kit ships roles, not colors: it reads `--ui-kit-*` and the application fills them.
 * This block is the only place where a role is bound to a project token.
 *
 * It sits OUTSIDE `@layer ui-kit` — Emotion writes unlayered rules — and an unlayered rule beats a
 * layered one whatever its specificity. That is what makes the binding win over the kit's neutral
 * defaults without a single `!important`.
 *
 * A severity role is filled WHOLE — surface, hover, pressed, text. Filling the surface alone leaves
 * the component falling back to a default hover that no longer goes with it. Pressed states are one
 * step past the direction, which names a rest and a hover only: they are derived here rather than
 * invented in `theme.ts`.
 */
const pressed = (color: string): string => `color-mix(in srgb, ${color} 82%, #000)`;

export const uiKitTokens: SerializedStyles = css`
	:root {
		/* ─── Action principale ─────────────────────────────────────────── */
		--ui-kit-primary: ${theme.colors.accent};
		--ui-kit-primary-hover: ${theme.colors.accentDark};
		--ui-kit-primary-active: ${pressed(theme.colors.accentDark)};
		--ui-kit-primary-text: ${theme.colors.surface};

		/* Action neutre : la pilule claire de la direction, d'où un texte foncé.
		   Le jeton -text est l'encre posée SUR le porteur, en apparence pleine ; -ink est celle posée
		   sur une surface, quand outlined / text / link retirent le fond. Les deux sont nécessaires
		   ici, et seulement ici : ce rôle est le seul dont le porteur est une teinte de surface, donc
		   le seul que le repli du kit — le porteur lui-même — rendrait illisible sur du blanc. */
		--ui-kit-secondary: ${theme.colors.surfaceMuted};
		--ui-kit-secondary-hover: ${theme.colors.trackMuted};
		--ui-kit-secondary-active: ${theme.colors.track};
		--ui-kit-secondary-text: ${theme.colors.ink};
		--ui-kit-secondary-ink: ${theme.colors.ink};

		/* ─── Sévérités ─────────────────────────────────────────────────── */
		/* La surface pleine prend l'APLAT de la paire, ni sa surface ni son encre : la surface ne
		   tient pas le seuil AA sous un texte, et l'encre d'avertissement non plus. Les blocs teintés
		   de la direction se retrouvent quand même — un Message mélange son rôle à 12 % sur le fond. */
		--ui-kit-success: ${theme.states.success.solid};
		--ui-kit-success-hover: ${pressed(theme.states.success.solid)};
		--ui-kit-success-active: ${pressed(pressed(theme.states.success.solid))};
		--ui-kit-success-text: ${theme.colors.surface};

		--ui-kit-warning: ${theme.states.warning.solid};
		--ui-kit-warning-hover: ${pressed(theme.states.warning.solid)};
		--ui-kit-warning-active: ${pressed(pressed(theme.states.warning.solid))};
		--ui-kit-warning-text: ${theme.colors.surface};

		--ui-kit-danger: ${theme.states.danger.solid};
		--ui-kit-danger-hover: ${pressed(theme.states.danger.solid)};
		--ui-kit-danger-active: ${pressed(pressed(theme.states.danger.solid))};
		--ui-kit-danger-text: ${theme.colors.surface};

		/* L'accent de la direction est déjà un bleu-vert : une information neutre s'y dit en gris,
		   sans quoi elle se confondrait avec l'action principale. */
		--ui-kit-info: ${theme.colors.inkSecondary};
		--ui-kit-info-hover: ${pressed(theme.colors.inkSecondary)};
		--ui-kit-info-active: ${pressed(pressed(theme.colors.inkSecondary))};
		--ui-kit-info-text: ${theme.colors.surface};

		/* Action annexe : l'étiquette bleu-vert clair de la direction. */
		--ui-kit-help: ${theme.tints.teal.surface};
		--ui-kit-help-hover: ${theme.colors.accentSurfaceStrong};
		--ui-kit-help-active: ${theme.colors.track};
		--ui-kit-help-text: ${theme.tints.teal.ink};

		/* Surface inversée : elle s'ÉCLAIRCIT au survol, seul rôle à inverser le mouvement. */
		--ui-kit-contrast: ${theme.colors.surfaceInverted};
		--ui-kit-contrast-hover: color-mix(in srgb, ${theme.colors.surfaceInverted} 88%, #fff);
		--ui-kit-contrast-active: color-mix(in srgb, ${theme.colors.surfaceInverted} 76%, #fff);
		--ui-kit-contrast-text: ${theme.colors.inkOnDark};

		/* ─── Texte, surfaces, filets ───────────────────────────────────── */
		--ui-kit-text: ${theme.colors.ink};
		--ui-kit-text-secondary: ${theme.colors.inkMuted};
		--ui-kit-text-disabled: ${theme.colors.inkFaint};

		--ui-kit-surface: ${theme.colors.surface};
		--ui-kit-surface-ground: ${theme.colors.canvas};
		--ui-kit-surface-hover: ${theme.colors.surfaceMuted};
		--ui-kit-surface-overlay: ${theme.colors.surface};

		/* Le filet d'un champ est le fort : le filet de carte (.07) disparaît sur le gris de fond. */
		--ui-kit-border: ${theme.colors.borderStrong};
		--ui-kit-border-hover: color-mix(in srgb, ${theme.colors.ink} 24%, transparent);

		--ui-kit-highlight-bg: ${theme.colors.surfaceMuted};

		/* ─── Sélection ─────────────────────────────────────────────────── */
		/* Ce qui doit se repérer : le porteur suffit, les trois peintures en dérivent. */
		--ui-kit-selected: ${theme.colors.accent};
		--ui-kit-selected-text: ${theme.colors.accentDark};
		/* Ce qui est plein — un contrôle enfoncé. Famille indépendante de la précédente. */
		--ui-kit-selected-solid: ${theme.colors.accent};
		--ui-kit-selected-solid-hover: ${theme.colors.accentDark};
		--ui-kit-selected-solid-text: ${theme.colors.surface};

		--ui-kit-mask-bg: color-mix(in srgb, ${theme.colors.ink} 42%, transparent);

		/* ─── Typographie ───────────────────────────────────────────────── */
		--ui-kit-font-family: ${theme.fonts.body};
		--ui-kit-font-size-sm: ${theme.fontSizes.small};
		--ui-kit-font-size-md: ${theme.fontSizes.medium};
		--ui-kit-font-size-lg: ${theme.fontSizes.large};
		--ui-kit-font-weight-normal: ${theme.fontWeights.normal};
		--ui-kit-font-weight-semibold: ${theme.fontWeights.semibold};

		/* ─── Formes et rythme ──────────────────────────────────────────── */
		--ui-kit-radius-sm: ${theme.radii.xsmall};
		--ui-kit-radius: ${theme.radii.medium};
		--ui-kit-radius-lg: ${theme.radii.xlarge};
		--ui-kit-radius-pill: ${theme.radii.pill};

		/* Le côté d'une case à cocher ne se lit pas au rayon de base : à 16 px sur 24 px de côté,
		   la case devient un rond. Sa variable n'est pas déclarée sur l'élément, donc la racine
		   du document l'atteint. */
		--ui-kit-checkbox-radius: ${theme.radii.xsmall};

		--ui-kit-transition: ${theme.motion.duration};
		--ui-kit-focus-ring: 0 0 0 0.2rem
			color-mix(in srgb, ${theme.colors.accent} 25%, transparent);

		/* La direction ne porte aucune ombre : une carte se détache par sa surface et son filet.
		   Une SURCOUCHE, elle, passe par-dessus un contenu qu'elle ne remplace pas — elle garde une
		   ombre, à peine posée, teintée de l'encre du projet. */
		--ui-kit-shadow-card: none;
		--ui-kit-shadow-overlay: 0 18px 40px -24px
			color-mix(in srgb, ${theme.colors.ink} 45%, transparent);

		/* ─── Contrôles ─────────────────────────────────────────────────── */
		--ui-kit-control-height-sm: ${theme.controls.heightSmall};
		--ui-kit-control-height: ${theme.controls.height};
		--ui-kit-control-height-lg: ${theme.controls.heightLarge};
		--ui-kit-control-padding: ${theme.controls.padding};

		/* ─── Formulaires ───────────────────────────────────────────────────  */
		--ui-kit-field-input-padding: 0.75rem;

		/* ─── Cards ───────────────────────────────────────────────────  */
		--ui-kit-card-body-padding: 1.375rem 1.5rem;

		/* ─── DataView ──────────────────────────────────────────────────── */
		/* Not declared anywhere in the kit's own layer — only read as var(x, fallback) — so a
		   plain :root binding here is the only source of truth for the component: the direction's
		   cards sit directly on the page canvas, with no frame around the grid. */
		--ui-kit-data-view-border: transparent;
		--ui-kit-data-view-radius: 0;
		--ui-kit-data-view-content-bg: transparent;
		--ui-kit-data-view-header-bg: transparent;

		/* These ARE declared on :root inside the kit's own layer, so this unlayered :root still
		   wins — but only while no [data-size]/[data-gutter] variant re-declares them directly
		   on the component root, which FacilitiesDataView does not use. */
		--ui-kit-data-view-content-padding: .5rem 0;
		--ui-kit-data-view-grid-gap: 1.25rem;
		--ui-kit-data-view-loading-overlay-bg: color-mix(in srgb, ${theme.colors.ink} 42%, transparent);

	}
	/* ─── Buttons ───────────────────────────────────────────────────  */
	.ui-kit-button[data-severity="danger"] {
		--ui-kit-button-border: ${theme.states.danger.surface};
		--ui-kit-button-bg-hover: color-mix(in srgb, ${theme.states.danger.ink} 7%, transparent);
		--ui-kit-button-border-active: ${theme.states.danger.surface};
		--ui-kit-button-text: ${theme.states.danger.ink};
		--ui-kit-button-text-hover: ${theme.states.danger.solid};
	}

	.ui-kit-button[data-severity="secondary"] {
		--ui-kit-button-border: ${theme.colors.surfaceMuted};
		--ui-kit-button-border-active: ${theme.colors.track};
		--ui-kit-button-text: ${theme.colors.inkSecondary};
		--ui-kit-button-text-hover: ${theme.colors.accent};
	}


	/* ─── Paginator ───────────────────────────────────────────────────  */
	.ui-kit-paginator {
		flex-wrap: nowrap;
	}
`;

/**
 * Ce que `:root` ne peut pas atteindre.
 *
 * Une variable que le kit DÉCLARE sur la racine d'un composant bat toute déclaration de `:root` :
 * c'est le prix des variantes, et le rayon d'un bouton en est une. La direction ne connaît pourtant
 * qu'une forme de bouton — la pilule —, ce qui se pose donc sur l'élément.
 *
 * Ce bloc est écrit DANS `@layer ui-kit`, à l'inverse de la liaison des rôles : à l'intérieur de la
 * couche, la cascade ordinaire reprend, et les règles de variante du kit — dont le bouton rond, plus
 * spécifique — continuent de gagner. Écrit hors couche, il les écraserait toutes.
 */
export const uiKitComponentDefaults: SerializedStyles = css`
	@layer ui-kit {
		.ui-kit-button {
			--ui-kit-button-radius: var(--ui-kit-radius-pill);
		}

		.ui-kit-tag {
			--ui-kit-tag-radius: var(--ui-kit-radius-pill);
		}

		/* The kit declares this variable directly on .ui-kit-sidebar — same specificity as this
		   rule, so source order decides, and this stylesheet is mounted after the kit's own.
		   --ui-kit-sidebar-width is only read by the left/right position rules; top/bottom
		   reads --ui-kit-sidebar-height instead, which this rule never touches. */
		.ui-kit-sidebar {
			--ui-kit-sidebar-width: ${theme.layout.sidebarWidth};

			.ui-kit-sidebar-header{
				.ui-kit-sidebar-title{
					margin: .5rem 0;
					padding-left: 1.25rem;

				}
			}
		}

		/*
			Modal -- Confirmation Modal
		 */
		.ui-kit-dialog.ui-kit-confirm-dialog {
			margin: 2rem;
		}


		/* The kit declares this variable directly on .ui-kit-field — same specificity as this rule, so
		   source order decides, and this stylesheet is mounted after the kit's own.
		   --ui-kit-field-padding is only read by the field's input and textarea; the field itself
		   never reads it.
		 */
		.ui-kit-field {
			margin: 0 1.25rem;
		}

		/*
			Dropdown
		 */
		.ui-kit-dropdown-panel {
			border: 1px solid ${theme.colors.borderStrong};

			.ui-kit-dropdown-header {
				--ui-kit-dropdown-header-padding: 0.5rem 1.25rem;
			}

			.ui-kit-dropdown-list[data-empty]{
				--ui-kit-dropdown-list-padding : 0;
			}

			.ui-kit-dropdown-list{
				--ui-kit-dropdown-list-padding : 0;
			}

			.ui-kit-dropdown-input{
				--ui-kit-dropdown-control-gap: 1rem;
			}
		}

		/*
			Multiselect
		 */
		.ui-kit-multi-select-panel {
			.ui-kit-multi-select-header {
				--ui-kit-multi-select-header-padding: 1rem;
			}
		}

		/*
			Autocomplete
		 */
		.ui-kit-field.ui-kit-autocomplete {
			--ui-kit-field-input-padding: 0 .75rem;
		}

		.ui-kit-autocomplete-panel{
			--ui-kit-autocomplete-panel-padding: 0;
			.ui-kit-autocomplete-item{
				--ui-kit-autocomplete-item-padding: .975rem 1rem;
			}
		}

		/*
			Checkbox
		 */
		.ui-kit-checkbox {
			.ui-kit-field-sub-root {
				align-items: center;
			}
		}

		/*
			Confirmation Modal
		 */
		.ui-kit-confirm-delete-dialog{
			.ui-kit-dialog-content{
				.ui-kit-confirm-delete-dialog-confirm-field{
					.ui-kit-field{
						margin: 0;
					}
				}
			}
		}



		/*
			Toast
		 */
		/* A dark surface: the severity lives in the icon alone, so no border, no tinted background.
		   The kit declares these variables on .ui-kit-toast-message — same specificity, source order
		   decides. */
		.ui-kit-toast-message {
			--ui-kit-toast-bg: ${theme.colors.surfaceInverted};
			--ui-kit-toast-text: ${theme.colors.inkOnDark};
			--ui-kit-toast-icon-text: ${theme.colors.accentBright};
			--ui-kit-toast-close-button-text: ${theme.colors.inkOnDarkMuted};
			--ui-kit-toast-close-button-bg-hover: color-mix(in srgb, ${theme.colors.inkOnDark} 10%, transparent);
			--ui-kit-toast-close-button-shadow-focus: 0 0 0 0.2rem
				color-mix(in srgb, ${theme.colors.accentBright} 45%, transparent);
		}

		.ui-kit-toast-message[data-severity="success"] {
			--ui-kit-toast-icon-text: ${theme.states.success.bright};
		}

		.ui-kit-toast-message[data-severity="warn"] {
			--ui-kit-toast-icon-text: ${theme.states.warning.bright};
		}

		.ui-kit-toast-message[data-severity="error"] {
			--ui-kit-toast-icon-text: ${theme.states.danger.bright};
		}

		.ui-kit-toast-close-button:hover {
			--ui-kit-toast-close-button-text: ${theme.colors.inkOnDark};
		}

		/* The kit has no color variable for the detail line. An opaque mix of inkOnDark into the surface keeps it AA-legible. */
		.ui-kit-toast-detail {
			color: color-mix(in srgb, ${theme.colors.inkOnDark} 68%, ${theme.colors.surfaceInverted});
		}

		/* Below the compact breakpoint the panel goes full width, whichever side it opens from.
		   Scoped to left/right — the only positions that consume the width variable at all. */
		@media screen and (max-width: ${theme.layout.compactBreakpoint}) {
			.ui-kit-sidebar[data-position='left'],
			.ui-kit-sidebar[data-position='right'] {
				--ui-kit-sidebar-width: 100%;
			}
		}
	}
`;
